import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from '../auth/auth.service';

/** Idle time with no user interaction before the warning modal opens. */ 
export const SESSION_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
/** Countdown after the warning opens before automatic sign-out. */ 
export const SESSION_WARNING_GRACE_MS = 60 * 1000;

/** Throttle high-frequency pointer moves so activity still resets idle without excess work. */
const MOUSEMOVE_THROTTLE_MS = 1000;

@Injectable({ providedIn: 'root' })
export class SessionTimerService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly auth = inject(AuthService);

  private readonly lastActivityAt = signal(Date.now());
  private readonly warningDeadlineAt = signal<number | null>(null);

  readonly warningOpen = signal(false);
  /** Updated every second by the expiry ticker for countdown UI. */
  private readonly now = signal(Date.now());

  /** When the idle window would elapse if the user stopped interacting now. */
  readonly expiresAt = computed(() => this.lastActivityAt() + SESSION_IDLE_TIMEOUT_MS);

  readonly remainingSeconds = computed(() => {
    this.now();
    const deadline = this.warningDeadlineAt();
    if (this.warningOpen() && deadline !== null) {
      return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    }
    return Math.max(0, Math.ceil((this.expiresAt() - Date.now()) / 1000));
  });

  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastMouseMoveHandledAt = 0;
  private readonly activityHandler = (): void => {
    this.recordActivity();
  };
  private readonly mouseMoveHandler = (): void => {
    const ts = Date.now();
    if (ts - this.lastMouseMoveHandledAt < MOUSEMOVE_THROTTLE_MS) {
      return;
    }
    this.lastMouseMoveHandledAt = ts;
    this.recordActivity();
  };

  constructor() {
    this.startTicker();
    this.bindActivityListeners();
    this.destroyRef.onDestroy(() => {
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      this.unbindActivityListeners();
    });
  }

  /** User explicitly chose to stay signed in (Extend session control only). */
  extendSession(): void {
    this.applySessionExtension();
  }

  /** Closes the warning modal without extending; sign-out still occurs when the grace period ends. */
  dismissWarningModal(): void {
    this.warningOpen.set(false);
  }

  /** Clears an in-progress warning/grace flow (explicit sign-out). */
  clearPendingExpiry(): void {
    this.warningOpen.set(false);
    this.warningDeadlineAt.set(null);
  }

  /** Resets idle tracking when the user interacts with the app (not during warning/grace). */
  recordActivity(): void {
    if (this.warningOpen() || this.warningDeadlineAt() !== null) {
      return;
    }
    this.applySessionExtension();
  }

  private applySessionExtension(): void {
    const now = Date.now();
    this.now.set(now);
    this.lastActivityAt.set(now);
    this.warningOpen.set(false);
    this.warningDeadlineAt.set(null);
  }

  /** Test hook — force the warning modal open. */
  forceWarning(): void {
    const now = Date.now();
    this.now.set(now);
    this.lastActivityAt.set(now - SESSION_IDLE_TIMEOUT_MS);
    this.warningOpen.set(true);
    this.warningDeadlineAt.set(now + SESSION_WARNING_GRACE_MS);
  }

  private startTicker(): void {
    this.intervalId = setInterval(() => this.evaluateExpiry(), 1_000);
  }

  private evaluateExpiry(): void {
    const now = Date.now();
    this.now.set(now);

    const deadline = this.warningDeadlineAt();
    if (deadline !== null) {
      if (now >= deadline) {
        this.clearPendingExpiry();
        void this.auth.logout();
      }
      return;
    }

    const idleMs = now - this.lastActivityAt();
    if (idleMs >= SESSION_IDLE_TIMEOUT_MS) {
      this.warningOpen.set(true);
      this.warningDeadlineAt.set(now + SESSION_WARNING_GRACE_MS);
    }
  }

  private bindActivityListeners(): void {
    const doc = this.document;
    if (!doc?.addEventListener) {
      return;
    }
    const opts: AddEventListenerOptions = { passive: true, capture: true };
    const events = ['keydown', 'mousedown', 'click', 'touchstart', 'scroll'] as const;
    for (const event of events) {
      doc.addEventListener(event, this.activityHandler, opts);
    }
    doc.addEventListener('mousemove', this.mouseMoveHandler, opts);
  }

  private unbindActivityListeners(): void {
    const doc = this.document;
    if (!doc?.removeEventListener) {
      return;
    }
    const opts: AddEventListenerOptions = { capture: true };
    const events = ['keydown', 'mousedown', 'click', 'touchstart', 'scroll'] as const;
    for (const event of events) {
      doc.removeEventListener(event, this.activityHandler, opts);
    }
    doc.removeEventListener('mousemove', this.mouseMoveHandler, opts);
  }
}
